import { RJSFSchema, UiSchema } from "@rjsf/utils";
import { formatSchemaName } from "./formatSchemaName";

// =============================================================================
// Types & Interfaces
// =============================================================================

export interface FieldMapping {
  widget?: string;
  fullWidth?: boolean;
  colSpan?: number;
  breakAfter?: boolean;
  order?: number;
  hidden?: boolean;
  placeholder?: string;
  options?: Record<string, unknown>;
  items?: Record<string, FieldMapping>;
  properties?: Record<string, FieldMapping>;
}

interface ColumnDefinition {
  key: string;
  span: number;
}

interface LayoutState {
  columns: ColumnDefinition[];
  currentRowSpan: number;
}

interface SchemaContext {
  rootSchema?: RJSFSchema;
  globalMapping: Record<string, FieldMapping>;
}

// =============================================================================
// Constants
// =============================================================================

const GRID_COLUMNS = 12;
const DEFAULT_COLUMN_SPAN = 4;
const DEFAULT_ORDER = 9999;

const WIDGETS = {
  HIDDEN: "hidden",
  SELECT: "SelectWidget",
  DATE_TIME: "DateTimeWidget",
  DATE: "DateWidget",
  TIME: "TimeWidget",
  TAG_SELECT: "TagSelectWidget",
} as const;

const TIME_PATTERN = "^\\d{2}:\\d{2}$";

// =============================================================================
// Schema Type Helpers
// =============================================================================

function isTypeMatch(schema: RJSFSchema, targetType: string): boolean {
  if (schema.type === targetType) return true;
  if (Array.isArray(schema.type)) return schema.type.includes(targetType as never);
  return false;
}

const isArrayType = (schema: RJSFSchema): boolean => isTypeMatch(schema, "array");
const isObjectType = (schema: RJSFSchema): boolean => isTypeMatch(schema, "object");
const isStringType = (schema: RJSFSchema): boolean => isTypeMatch(schema, "string");

// =============================================================================
// Reference Resolution
// =============================================================================

function resolveRef(ref: string, rootSchema?: RJSFSchema): RJSFSchema | undefined {
  if (!rootSchema?.definitions || !ref.startsWith("#/definitions/")) {
    return undefined;
  }
  const definitionName = ref.replace("#/definitions/", "");
  return rootSchema.definitions[definitionName] as RJSFSchema | undefined;
}

function resolveSchemaWithRef(
  schema: RJSFSchema,
  rootSchema?: RJSFSchema
): { resolved: RJSFSchema; refName: string | null } {
  if (!schema.$ref) {
    return { resolved: schema, refName: null };
  }

  const refName = schema.$ref.replace("#/definitions/", "");
  const resolved = resolveRef(schema.$ref, rootSchema);

  return {
    resolved: resolved ?? schema,
    refName,
  };
}

// =============================================================================
// Widget Detection
// =============================================================================

function detectWidget(schema: RJSFSchema, mapping: FieldMapping): string | undefined {
  // Explicit widget override takes precedence
  if (mapping.widget) {
    return mapping.widget;
  }

  // Enum fields use select widget
  if (schema.enum) {
    return WIDGETS.SELECT;
  }

  // oneOf fields (like those with const/title pairs) also use select widget
  if (schema.oneOf) {
    return WIDGETS.SELECT;
  }

  // String type with format or pattern
  if (isStringType(schema)) {
    if (schema.format === "date-time") return WIDGETS.DATE_TIME;
    if (schema.format === "date") return WIDGETS.DATE;
    if (schema.pattern === TIME_PATTERN) return WIDGETS.TIME;
  }

  // Array with enum items uses tag select
  if (isArrayType(schema)) {
    const items = schema.items as RJSFSchema | undefined;
    if (items?.enum) {
      return WIDGETS.TAG_SELECT;
    }
  }

  return undefined;
}

// =============================================================================
// Layout Helpers
// =============================================================================

function calculateColumnSpan(
  schema: RJSFSchema,
  mapping: FieldMapping,
  widgetType?: string
): number {
  if (mapping.colSpan) {
    return mapping.colSpan;
  }

  const shouldBeFullWidth =
    mapping.fullWidth ||
    isArrayType(schema) ||
    widgetType === WIDGETS.TAG_SELECT ||
    (isStringType(schema) && schema.maxLength && schema.maxLength > 100);

  return shouldBeFullWidth ? GRID_COLUMNS : DEFAULT_COLUMN_SPAN;
}

function addColumnToLayout(
  layout: LayoutState,
  key: string,
  span: number,
  breakAfter: boolean
): void {
  // Start new row if current column doesn't fit
  if (layout.currentRowSpan + span > GRID_COLUMNS) {
    layout.currentRowSpan = 0;
  }

  layout.columns.push({ key, span });
  layout.currentRowSpan += span;

  // Add spacer if break is requested
  if (breakAfter) {
    const remaining = GRID_COLUMNS - layout.currentRowSpan;
    if (remaining > 0) {
      layout.columns.push({ key: `__spacer_${key}`, span: remaining });
    }
    layout.currentRowSpan = 0;
  }
}

function buildLayoutGrid(columns: ColumnDefinition[]): UiSchema["ui:layoutGrid"] {
  if (columns.length === 0) {
    return { "ui:row": [] };
  }

  return {
    "ui:row": [
      {
        "ui:row": {
          spacing: 2,
          className: "row",
          justifyContent: "flex-start",
          size: { md: GRID_COLUMNS },
          children: columns.map((col) => ({
            "ui:col": {
              children: [col.key],
              size: { md: col.span },
            },
          })),
        },
      },
    ],
  };
}

// =============================================================================
// Property Key Sorting
// =============================================================================

function getSortedPropertyKeys(
  schema: RJSFSchema,
  mapping: Record<string, FieldMapping>
): string[] {
  const schemaKeys = Object.keys(schema.properties ?? {});
  const mappingKeys = Object.keys(mapping);

  // Combine unique keys from both sources
  const allKeys = Array.from(new Set([...schemaKeys, ...mappingKeys]));

  return allKeys.sort((a, b) => {
    const orderA = mapping[a]?.order ?? getDefaultOrder(a, mappingKeys);
    const orderB = mapping[b]?.order ?? getDefaultOrder(b, mappingKeys);
    return orderA - orderB;
  });
}

function getDefaultOrder(key: string, mappingKeys: string[]): number {
  const index = mappingKeys.indexOf(key);
  return index !== -1 ? index : DEFAULT_ORDER;
}

// =============================================================================
// Mapping Merge Utilities
// =============================================================================

function mergeFieldMappings(
  definitionMapping: FieldMapping,
  specificMapping: Record<string, FieldMapping>
): Record<string, FieldMapping> {
  const baseMapping = definitionMapping.properties ?? {};
  return { ...baseMapping, ...specificMapping };
}

function getDefinitionMapping(
  refName: string | null,
  globalMapping: Record<string, FieldMapping>
): FieldMapping {
  return refName ? (globalMapping[refName] ?? {}) : {};
}

// =============================================================================
// Nested Schema Handlers
// =============================================================================

function processArrayItems(
  key: string,
  schema: RJSFSchema,
  fieldMapping: FieldMapping,
  context: SchemaContext,
  uiSchema: UiSchema
): void {
  if (!schema.items) return;

  let itemsSchema = schema.items as RJSFSchema;
  let itemsRefName: string | null = key;

  // Resolve ref in items schema
  if (itemsSchema.$ref) {
    itemsRefName = itemsSchema.$ref.replace("#/definitions/", "");
    const resolved = resolveRef(itemsSchema.$ref, context.rootSchema);
    if (resolved) {
      itemsSchema = resolved;
    }
  }

  const itemsIsObject =
    isObjectType(itemsSchema) && itemsSchema.properties !== undefined;

  if (!itemsIsObject) return;

  const definitionMapping = getDefinitionMapping(itemsRefName, context.globalMapping);
  const mergedMapping = mergeFieldMappings(
    definitionMapping,
    fieldMapping.items ?? {}
  );

  const itemsUiSchema = generateUiSchema(
    itemsSchema,
    mergedMapping,
    context.rootSchema,
    context.globalMapping
  );

  uiSchema[key] = {
    ...uiSchema[key],
    items: itemsUiSchema,
  };
}

function processNestedObject(
  key: string,
  schema: RJSFSchema,
  refName: string | null,
  fieldMapping: FieldMapping,
  context: SchemaContext,
  uiSchema: UiSchema,
  layout: LayoutState
): boolean {
  if (!schema.properties) return false;

  const definitionMapping = getDefinitionMapping(refName, context.globalMapping);
  const mergedMapping = mergeFieldMappings(
    definitionMapping,
    fieldMapping.properties ?? {}
  );

  const nestedUiSchema = generateUiSchema(
    schema,
    mergedMapping,
    context.rootSchema,
    context.globalMapping
  );

  // Extract and flatten nested layout columns
  const nestedRow = nestedUiSchema["ui:layoutGrid"]?.["ui:row"]?.[0]?.["ui:row"];
  if (nestedRow?.children) {
    flattenNestedColumns(key, nestedRow.children, layout);
  }

  // Copy nested uiSchema properties (excluding layout metadata)
  const {
    "ui:field": _field,
    "ui:submitButtonOptions": _submit,
    "ui:layoutGrid": _layout,
    ...nestedProps
  } = nestedUiSchema;

  uiSchema[key] = {
    ...uiSchema[key],
    ...nestedProps,
  };

  return true; // Indicates field was flattened
}

function flattenNestedColumns(
  parentKey: string,
  children: Array<{ "ui:col": { children: string[]; size: { md: number } } }>,
  layout: LayoutState
): void {
  for (const col of children) {
    const colDef = col["ui:col"];
    if (!colDef?.children) continue;

    for (const childKey of colDef.children) {
      // Skip spacer elements
      if (childKey.startsWith("__spacer_")) continue;

      const fullKey = `${parentKey}.${childKey}`;
      const span = colDef.size?.md ?? DEFAULT_COLUMN_SPAN;

      addColumnToLayout(layout, fullKey, span, false);
    }
  }
}

// =============================================================================
// Field Processing
// =============================================================================

function processField(
  key: string,
  schema: RJSFSchema,
  fieldMapping: FieldMapping,
  context: SchemaContext,
  uiSchema: UiSchema,
  layout: LayoutState
): void {
  // Handle hidden fields
  if (fieldMapping.hidden) {
    uiSchema[key] = { "ui:widget": WIDGETS.HIDDEN };
    return;
  }

  // Resolve any $ref in the field schema
  const { resolved: fieldSchema, refName } = resolveSchemaWithRef(
    schema,
    context.rootSchema
  );

  // Set default title formatting if not present
  if (!uiSchema[key]) uiSchema[key] = {};
  if (!fieldSchema.title && !uiSchema[key]["ui:title"]) {
    uiSchema[key]["ui:title"] = formatSchemaName(key);
  }

  // Detect and apply widget
  const widget = detectWidget(fieldSchema, fieldMapping);
  if (widget) {
    uiSchema[key] = { ...uiSchema[key], "ui:widget": widget };
  }

  // Apply custom options
  if (fieldMapping.options) {
    uiSchema[key] = { ...uiSchema[key], "ui:options": fieldMapping.options };
  }

  // Handle array items (nested objects in arrays)
  if (isArrayType(fieldSchema)) {
    processArrayItems(key, fieldSchema, fieldMapping, context, uiSchema);
  }

  // Handle nested objects - flatten their fields into parent layout
  if (isObjectType(fieldSchema)) {
    const wasFlattened = processNestedObject(
      key,
      fieldSchema,
      refName,
      fieldMapping,
      context,
      uiSchema,
      layout
    );
    if (wasFlattened) return;
  }

  // Add field to layout grid
  const span = calculateColumnSpan(fieldSchema, fieldMapping, widget);
  addColumnToLayout(layout, key, span, fieldMapping.breakAfter ?? false);
}

// =============================================================================
// Main Export
// =============================================================================

/**
 * Generates a UI schema for react-jsonschema-form with layout grid support.
 *
 * @param schema - The JSON schema to generate UI schema for
 * @param mapping - Field-specific UI configuration mappings
 * @param rootSchema - Root schema for resolving $ref definitions (auto-detected if omitted)
 * @param globalMapping - Global mapping for definition lookups (defaults to mapping)
 * @returns Complete UI schema with layout grid configuration
 */
export function generateUiSchema(
  schema: RJSFSchema,
  mapping: Record<string, FieldMapping> = {},
  rootSchema?: RJSFSchema,
  globalMapping?: Record<string, FieldMapping>
): UiSchema {
  // Initialize context
  const context: SchemaContext = {
    rootSchema: rootSchema ?? (schema.definitions ? schema : undefined),
    globalMapping: globalMapping ?? mapping,
  };

  // Initialize base UI schema
  const uiSchema: UiSchema = {
    "ui:field": "LayoutGridField",
    "ui:submitButtonOptions": { norender: true },
    "ui:layoutGrid": { "ui:row": [] },
  };

  // Initialize layout tracking
  const layout: LayoutState = {
    columns: [],
    currentRowSpan: 0,
  };

  // Process fields in sorted order
  const sortedKeys = getSortedPropertyKeys(schema, mapping);

  for (const key of sortedKeys) {
    const fieldSchema = (schema.properties?.[key] ?? {}) as RJSFSchema;
    const fieldMapping = mapping[key] ?? {};

    processField(key, fieldSchema, fieldMapping, context, uiSchema, layout);
  }

  // Build final layout grid
  uiSchema["ui:layoutGrid"] = buildLayoutGrid(layout.columns);

  return uiSchema;
}